import { useContext, useEffect, useState } from "react";

import LocationContext from "@context/location/locationContext";
import SettingsContext from "@context/Settings/settingsContext";
import ShowErrorToastContext from "@context/showErrorToast/showErrorToastContext";
import useShowPropertyType from "@hooks/useShowPropertyType";
import { Grid, Skeleton } from "@mui/material";
import { PROPERTY_TYPE } from "@pages/Dashboard/HomeDashboard/TotalSales/helper";
import SharedCard from "@shared-layouts/ShardCard";
import { TypographyH5 } from "@shared-ui-components/CustomTypography";
import LineChart from "@shared-ui-components/LineChart";
import { message } from "@src/constants";

import LastUpdated from "@src/components/shared-containers/LastUpdated";
import useShowPropertyStoreRevType from "@src/hooks/useShowPropertyStoreRevType";
import { LastUpdatedWrapper } from "../../HomeDashboard/style";
import {
  getRevenueData,
  getRevenueGraphData,
  getRevenueTotalSalesMapData,
  getSalesMapped,
  getTimeMapped,
  initialDropDownOptions,
  initialRevenueDashboardData,
  initialRevenueTotalSalesData,
  TOTAL_SALES_VIEW_VALUE,
} from "./helper";
import {
  EventTitleWrapper,
  TotalSalesCardWrapper,
  TotalSalesWrapper,
} from "./style";
import TotalSalesData from "./TotalSalesData";

const TotalSales = () => {
  const [activeTab, setActiveTab] = useState<string>("By Real-Time");

  const [revenueDashboardTotalSalesData, setRevenueDashboardTotalSalesData] =
    useState<RevenueDashboardDataType>(initialRevenueTotalSalesData);

  const [loading, setLoading] = useState<loaderType>({
    mainLoader: false,
    graphLoader: false,
  });
  const [dropDownOption, setDropDownOption] = useState<dropDownOptionType[]>(
    initialDropDownOptions
  );

  const [graphData, setGraphData] = useState<GraphDataType>({
    time: [],
    sales: [],
  });

  const initialState: TotalSalesDataType[] = getRevenueTotalSalesMapData(
    initialRevenueDashboardData,
    PROPERTY_TYPE?.ALL
  );

  const [totalSalesData, setTotalSalesData] =
    useState<TotalSalesDataType[]>(initialState);
  const [{ locationState }] = useContext(LocationContext);
  const { property, store, revenueCenter, date } = locationState;

  const [{ showErrorToastSetState }] = useContext(ShowErrorToastContext);

  const [{ settingsState }] = useContext(SettingsContext);

  const { planData, foreCastData } = settingsState;

  const hideDaypart = useShowPropertyStoreRevType(property, "hideDaypart");

  const isQsProperty = useShowPropertyStoreRevType(property, "isQsProperty");

  const handleTypeChange = (value: string) => {
    setActiveTab(value);
  };

  const getData = async (filters: RevenueFilterType) => {
    const graphRes = await getRevenueGraphData(filters);
    if (!graphRes.error) {
      const mappedTime = getTimeMapped(graphRes?.data?.data, activeTab);
      const mappedSales = getSalesMapped(graphRes?.data?.data, activeTab);
      if (mappedTime && mappedSales) {
        setGraphData({
          time: mappedTime,
          sales: mappedSales,
        });
      }
    } else {
      showErrorToastSetState({
        errorMessage: graphRes.error || "",
        errorTitle: message.ERROR_MESSAGE_DASHBOARD,
        open: true,
      });
    }
    setLoading((pre: loaderType) => ({ ...pre, graphLoader: false }));
  };

  const getRevenueDashboardData = async (filters: DashboardFilterType) => {
    const revenueRes = await getRevenueData(filters);
    if (!revenueRes?.error) {
      const mappedData: TotalSalesDataType[] = getRevenueTotalSalesMapData(
        revenueRes?.data,
        revenueRes?.data?.propertyType
      );

      const propertyRestaurantCateringRevenueCenters = useShowPropertyType(
        revenueRes?.data?.propertyType,
        "propertyRestaurantCateringRevenueCenters"
      );

      const quickServiceRevenueCenters = useShowPropertyType(
        revenueRes?.data?.propertyType,
        "quickServiceRevenueCenters"
      );

      const hideTargetValue =
        propertyRestaurantCateringRevenueCenters || quickServiceRevenueCenters;

      if (!hideTargetValue) {
        setRevenueDashboardTotalSalesData((pre) => {
          return { ...pre, targetValue: revenueRes?.data?.targetSales };
        });
      } else {
        setRevenueDashboardTotalSalesData((pre) => {
          return { ...pre, targetValue: null };
        });
      }

      setRevenueDashboardTotalSalesData((pre) => {
        return {
          ...pre,
          eventName: revenueRes?.data?.event_name || "",
          lastUpdated: revenueRes?.data?.last_updated,
        };
      });

      setTotalSalesData(mappedData);
    } else {
      showErrorToastSetState({
        errorMessage: revenueRes?.error || "",
        errorTitle: message.ERROR_MESSAGE_DASHBOARD,
        open: true,
      });
    }
    setLoading((pre: loaderType) => ({ ...pre, mainLoader: false }));
  };

  const showDropDownOptions = () => {
    if (hideDaypart) {
      const filteredDropDownOptions = initialDropDownOptions?.filter(
        (e) => e.value !== "daypart"
      );
      setDropDownOption(filteredDropDownOptions);
      return;
    }
    setDropDownOption(initialDropDownOptions);
  };

  useEffect(() => {
    setLoading((pre: loaderType) => ({ ...pre, graphLoader: true }));
    const filter: RevenueFilterType = {
      property,
      store,
      rvc: revenueCenter,
      date,
      filterBy: TOTAL_SALES_VIEW_VALUE[activeTab],
    };
    getData(filter);
    showDropDownOptions();
  }, [activeTab, property, store, revenueCenter, date]);

  useEffect(() => {
    setLoading((pre: loaderType) => ({ ...pre, mainLoader: true }));
    const filter: DashboardFilterType = {
      property,
      store,
      rvc: revenueCenter,
      date,
      planData,
      foreCastData,
    };
    getRevenueDashboardData(filter);
    showDropDownOptions();
  }, [property, store, revenueCenter, date]);

  return (
    <SharedCard
      title="Total Sales"
      handleTypeChange={handleTypeChange}
      dropDownOptions={dropDownOption}
      selectedValue={activeTab}
    >
      <TotalSalesWrapper>
        {revenueDashboardTotalSalesData?.eventName && (
          <EventTitleWrapper>
            <TypographyH5 className="customColor">Event:</TypographyH5>
            {loading?.mainLoader ? (
              <Skeleton width="70%" />
            ) : (
              <TypographyH5 className="customSubTitle">
                {revenueDashboardTotalSalesData?.eventName}
              </TypographyH5>
            )}
          </EventTitleWrapper>
        )}
        <LineChart
          loading={loading?.graphLoader}
          activeTab={activeTab}
          time={graphData?.time}
          sales={graphData?.sales}
          targetValue={revenueDashboardTotalSalesData?.targetValue}
          noData={
            revenueDashboardTotalSalesData?.targetValue === null ||
            graphData?.sales?.length === 0
          }
          isQsProperty={activeTab === "By Hour" && isQsProperty}
        />
        <TotalSalesCardWrapper>
          <Grid container>
            {totalSalesData?.map((i: TotalSalesDataType) => {
              return (
                <TotalSalesData
                  key={i?.id}
                  activeTab={activeTab}
                  mapData={i?.mapDataRevenue}
                  loading={loading?.mainLoader}
                />
              );
            })}
          </Grid>
        </TotalSalesCardWrapper>
        <LastUpdatedWrapper>
          <LastUpdated
            loading={loading?.mainLoader}
            lastUpdatedTime={revenueDashboardTotalSalesData?.lastUpdated}
          />
        </LastUpdatedWrapper>
      </TotalSalesWrapper>
    </SharedCard>
  );
};

export default TotalSales;
